import { cx } from '../primitives/cx.js';

/**
 * Inline SVG, `currentColor`, no icon library.
 *
 * A dependency like lucide-react is ~2KB per icon *plus* tree-shaking risk in
 * a monorepo; at this count, hand-rolled paths cost less than the import
 * machinery and guarantee the strokes match our 1.75 weight everywhere.
 */
const paths = {
  check: 'M4 12.5 9 17.5 20 6.5',
  plus: 'M12 5v14M5 12h14',
  home: 'M4 11.5 12 4l8 7.5V20a1 1 0 0 1-1 1h-4v-6H9v6H5a1 1 0 0 1-1-1z',
  map: 'M9 4 3 6.5v13L9 17l6 2.5 6-2.5v-13L15 6.5zM9 4v13M15 6.5v13',
  chart: 'M4 20h16M7 16V9M12 16V5M17 16v-4',
  /** The khata — a bound notebook seen from the spine side. */
  book: 'M5 4.5A1.5 1.5 0 0 1 6.5 3H19v15H6.5A1.5 1.5 0 0 0 5 19.5zM5 19.5A1.5 1.5 0 0 0 6.5 21H19v-3',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4.5 20a7.5 7.5 0 0 1 15 0',
  bolt: 'M13 3 5 13.5h5.5L10 21l8-10.5h-5.5z',
  flame: 'M12 3s5 4.2 5 9a5 5 0 0 1-10 0c0-1.6.7-2.9 1.5-3.8 0 1.5.8 2.3 1.7 2.3 1.2 0 1.8-1 1.8-2.6 0-2-1-4-1-4z',
  trophy: 'M7 4h10v5a5 5 0 0 1-10 0zM7 6H4v2a3 3 0 0 0 3 3M17 6h3v2a3 3 0 0 1-3 3M10 19h4M12 14v5',
  moon: 'M20 14.5A8 8 0 0 1 9.5 4 8.5 8.5 0 1 0 20 14.5Z',
  sun: 'M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10ZM12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4',
  share: 'M12 15V4M12 4 8.5 7.5M12 4l3.5 3.5M5 13v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5',
  download: 'M12 4v11M12 15l-3.5-3.5M12 15l3.5-3.5M5 19h14',
  arrowUp: 'M12 19V5M12 5l-6 6M12 5l6 6',
  arrowDown: 'M12 5v14M12 19l-6-6M12 19l6-6',
  /** Drag handle. Six dots, drawn as zero-length round strokes — the shape
   *  everyone already reads as "grab me", with no fill rule of its own. */
  grip: 'M9.5 7h.01M14.5 7h.01M9.5 12h.01M14.5 12h.01M9.5 17h.01M14.5 17h.01',
  x: 'M6 6l12 12M18 6 6 18',
  trash: 'M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-12M10 11v5M14 11v5',
} as const;

export type IconName = keyof typeof paths;

export type IconProps = {
  name: IconName;
  size?: number;
  className?: string;
  /** Icons are decorative by default; give a label only when it carries meaning. */
  label?: string;
};

export function Icon({ name, size = 20, className, label }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cx('shrink-0', className)}
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
      aria-label={label}
    >
      <path d={paths[name]} />
    </svg>
  );
}
